import getWeb3, { getWeb3IfAvailable, parseError, toNumber, toRealNumber, ether } from "./web3Utils";
import { getContractABI, getContractAddress } from "./utils/abiHelper";

export const getUserBalance = async () => {
  const web3 = await getWeb3IfAvailable();
  const accounts = await web3.eth.getAccounts();
  return toNumber( await web3.eth.getBalance(accounts[0]) );
}

export const getBlockChainNetworkId = async () => {
  const web3 = await getWeb3IfAvailable();
  return web3.eth.net.getId();
}

export const getUserAddress = async () => {
  const web3 = await getWeb3IfAvailable();
  const accounts = await web3.eth.getAccounts();
  return accounts && accounts.length ? accounts[0] : null;
}

export const claimVest = async ({ 
  vestId,
  contractType
}) => {
  const web3 = await getWeb3IfAvailable();
  const accounts = await web3.eth.getAccounts();
  const gasPrice = await web3.eth.getGasPrice();
  const contractAddress = await getContractAddress(contractType);

  if( accounts && !!accounts.length && contractAddress ) {
    
    const abi = getContractABI(contractType)
    const contractInstance = new web3.eth.Contract( abi, contractAddress );
  
    try {
      
      let estimatedGas = await contractInstance.methods.claimVest( 
        accounts[0],
        vestId,
      ).estimateGas({
        from: accounts[0]
      });
      
      await contractInstance.methods.claimVest(
        accounts[0],
        vestId,
      ).send({ 
        from: accounts[0],
        gasPrice,
        gas: estimatedGas + 5000
      });
    }
    catch(e) {
      const parsedErrorMessage = parseError(e);
      console.log('Caught error while claiming vest',e);
      if(parsedErrorMessage.includes('Already Claimed')) {
        // eslint-disable-next-line   no-throw-literal
        throw 'Already Claimed'
      } else {
        throw parsedErrorMessage
      }
    }
    return 'Vest claimed successfully'
  }
}

export const getClaim = async ({ 
  vestId,
  contractType
}) => {

  const web3 = await getWeb3IfAvailable();
  const accounts = await web3.eth.getAccounts();
  const contractAddress = await getContractAddress(contractType);

  if( !!vestId && 
      contractType  &&
      accounts &&
      contractAddress && 
      !!accounts.length ) {
    
    const abi = getContractABI(contractType)
    const contractInstance = new web3.eth.Contract( abi, contractAddress );
        
    try {
      const vest = await contractInstance.methods.getUserVest(
        accounts[0],
        vestId,
      ).call();

      return {
        releaseTime: toRealNumber(vest.releaseTime),
        amount: toNumber(vest.amount),
        claimedAmount: toNumber(vest.claimedAmount),
        isClaimed: vest.isClaimed,
      }
    }
    catch(e) {
      const parsedErrorMessage = parseError(e);
      console.log('Caught error while fetching vest', parsedErrorMessage);
      // eslint-disable-next-line   no-throw-literal
      throw 'Unable to fetch vest status'
    }
  } else {
    // eslint-disable-next-line   no-throw-literal
    throw 'Unable to fetch vest status'
  }
}

export const getTotalTokensAllocated = async ({
  contractType
}) => {

  const web3 = await getWeb3IfAvailable();
  const contractAddress = await getContractAddress(contractType);
  const abi = getContractABI(contractType);
  const contractInstance = new web3.eth.Contract( abi, contractAddress );
  const totalTokensAllocated = await contractInstance.methods.getTotalTokensAllocated().call();
  return toNumber(totalTokensAllocated);
}

export const getTokenBalance = async ({
  contractType = 'MonstersClanToken'
}) => {

  const web3 = await getWeb3IfAvailable();
  const contractAddress = await getContractAddress(contractType);
  const abi = getContractABI(contractType);
  const accounts = await web3.eth.getAccounts();
  const contractInstance = new web3.eth.Contract( abi, contractAddress );
  const balance = await contractInstance.methods.balanceOf(accounts[0]).call();
  return toNumber(balance);
}

export const getContractUser = async ({
  contractType
}) => {

  const web3 = await getWeb3IfAvailable();
  const contractAddress = await getContractAddress(contractType);
  const abi = getContractABI(contractType);
  const accounts = await web3.eth.getAccounts();
  const contractInstance = new web3.eth.Contract( abi, contractAddress );

  const user = await contractInstance.methods.getUser(
    accounts[0],
  ).call();

  return user;
}

export const getEthRaised = async ({
  contractType,
}) => {

  const web3 = await getWeb3IfAvailable();
  const contractAddress = await getContractAddress(contractType);
  const abi = getContractABI(contractType);
  const contractInstance = new web3.eth.Contract( abi, contractAddress );
  const getEthRaised = await contractInstance.methods.getTotalEthRaised().call();
  return toNumber(getEthRaised);
}

export const buyTokens = async ({
  contractType,
  value
}, onlyEstiamte = false ) => {

  const web3 = await getWeb3IfAvailable();
  const gasPrice = await web3.eth.getGasPrice();
  const accounts = await web3.eth.getAccounts();
  
  const contractAddress = await getContractAddress(contractType);

  const abi = getContractABI(contractType);
  const contractInstance = new web3.eth.Contract( abi, contractAddress );

  try {

    const estimatedGas = await contractInstance.methods.buyTokens( 
      accounts[0]
    ).estimateGas({
      from: accounts[0],
      value: new web3.utils.BN(web3.utils.toWei(value.toString(), 'ether')), 
    });
    
    if( !!onlyEstiamte ) {
      return estimatedGas;
    } else {
      await contractInstance
      .methods
      .buyTokens( accounts[0] )
      .send({ 
        from: accounts[0],
        gasPrice,
        gas: estimatedGas + 5000,
        value: new web3.utils.BN(web3.utils.toWei(value.toString(), 'ether')), 
      });
      return 'Tokens brought successfully';
    }
  } catch(e) {
    console.log('Caught error while buying tokens',e);
    const parsedErrorMessage = parseError(e);
    throw parsedErrorMessage
  }
};


export const getAssetInfo = async ({
  inventoryId,
  contractType = 'MonstersClanNftSale'
}) => {
  const web3 = await getWeb3IfAvailable();
  const contractAddress = await getContractAddress(contractType);
  const abi = getContractABI(contractType);
  if(!contractAddress) {
    return {
      quantity: 0,
      sold: 0,
      userCap: 0,
      rate: 0,
    }
  } else {
    const contractInstance = new web3.eth.Contract( abi, contractAddress );
    const assetInfo = await contractInstance.methods.getAssetInfo(inventoryId).call();
    return {
      quantity: toRealNumber(assetInfo.quantity),
      sold: toRealNumber(assetInfo.sold),
      userCap: toRealNumber(assetInfo.userCap),
      rate: toNumber(assetInfo.rate)
    }
  }
}

export const getUserAssetInfo = async ({
  inventoryId,
  contractType = 'MonstersClanNftSale'
}) => {
  const web3 = await getWeb3IfAvailable();
  const accounts = await web3.eth.getAccounts();
  const contractAddress = await getContractAddress(contractType);
  const abi = getContractABI(contractType);
  if(!contractAddress) {
    return {
      quantity: 0,
    }
  } else {
    const contractInstance = new web3.eth.Contract( abi, contractAddress );
    const userAssetInfo = await contractInstance.methods.getUserAssetInfo(accounts[0], inventoryId).call();
    return {
      quantity: toRealNumber(userAssetInfo.quantity),
    }
  }
}

export const getUsdPerToken = async ({
  contractType = 'MonstersClanNftSale'
}) => {
  const web3 = await getWeb3IfAvailable();
  const contractAddress = await getContractAddress(contractType);
  const abi = getContractABI(contractType);
  if(!contractAddress) {
    return 0
  } else {
    const contractInstance = new web3.eth.Contract( abi, contractAddress );
    const usdPerToken = await contractInstance.methods.usdPerToken().call();
    return toNumber(usdPerToken)
  }
}


export const buyNftTokens = async ({
  inventoryId,
  quantity = 1,
  contractType = 'MonstersClanNftSale',
  value
}, onlyEstiamte = false ) => {

  const web3 = await getWeb3IfAvailable();
  const gasPrice = await web3.eth.getGasPrice();
  const accounts = await web3.eth.getAccounts();
  
  const contractAddress = await getContractAddress(contractType);
  const abi = getContractABI(contractType);

  const contractInstance = new web3.eth.Contract( abi, contractAddress );
  
  if(!contractAddress) {
    throw new Error('Please switch to correct network.')
  }

  try {

    const estimatedGas = await contractInstance
      .methods
      .buyTokens( 
        accounts[0],
        inventoryId,
        quantity,
        ether(value), 
      ).estimateGas({
        from: accounts[0], 
      });
    
    if( !!onlyEstiamte ) {
      return estimatedGas;
    } else {
      await contractInstance
        .methods
        .buyTokens( 
          accounts[0],
          inventoryId,
          quantity,
          ether(value), 
        )
        .send({ 
          from: accounts[0],
          gasPrice,
          gas: estimatedGas + 5000
        });
      return 'Tokens brought successfully';
    }
  } catch(e) {
    console.log('Caught error while buying tokens',e);
    const parsedErrorMessage = parseError(e);
    throw parsedErrorMessage
  }
};


export const allowance = async ({
  contractType = 'MonstersClanToken',
  spenderContractType = 'MonstersClanNftSale'
}) => {

  const web3 = await getWeb3IfAvailable()
  const accounts = await web3.eth.getAccounts();

  const contractAddress = await getContractAddress(contractType);
  const spenderContractAddress = await getContractAddress(spenderContractType);
  const abi = getContractABI(contractType);
    
  const contractInstance = new web3.eth.Contract(
    abi,
    contractAddress
  );

  if(!contractAddress) {
    throw new Error('Please switch to correct network.')
  }

  try {

    const allowance = await contractInstance.methods.allowance(
      accounts[0],
      spenderContractAddress,
    ).call();

    return toNumber(allowance);
  } catch (e) {
    console.log('EVM Error', e);
    const parsedErrorMessage = parseError(e);
    throw parsedErrorMessage
  }
}

export const approveTokens = async ({
  value,
  contractType = 'MonstersClanToken',
  spenderContractType = 'MonstersClanNftSale'
}) => {

  const web3 = await getWeb3IfAvailable()
  const accounts = await web3.eth.getAccounts();
  const gasPrice = await web3.eth.getGasPrice();

  const contractAddress = await getContractAddress(contractType);
  const spenderContractAddress = await getContractAddress(spenderContractType);
  const abi = getContractABI(contractType);
    
  const contractInstance = new web3.eth.Contract(
    abi,
    contractAddress
  );

  if(!contractAddress) {
    throw new Error('Please switch to correct network.')
  }

  try {

    let estimatedGas = await contractInstance.methods
    .approve( 
      spenderContractAddress,
      ether(value).toString(),
    )
    .estimateGas({
      from: accounts[0]
    });

    await contractInstance.methods.approve(
      spenderContractAddress,
      ether(value).toString(),
    ).send({ from: accounts[0], gasPrice, gas: estimatedGas + 5000 });

  } catch (e) {
    console.log('EVM Error', e);
    const parsedErrorMessage = parseError(e);
    throw parsedErrorMessage
  }
  
  return 'Tokens approved successfully.'

}

export default getWeb3;